import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users,
  MoreVertical,
  MapPin,
  GraduationCap,
  Trophy,
  TrendingUp,
  Award,
  Plus,
  Loader2,
  Search,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getCoachLinkedProfiles,
  coachUnlinkProfile,
  searchJgsStudents as endUserSearch,
  coachLinkProfile,
} from "@/services/endUser";
import { useEndUser } from "@/contexts/EndUserContext";
import { useNavigate } from "react-router-dom";
import {
  trackCoachPlayerLinked,
  trackCoachPlayerUnlinked,
  trackCoachPlayerSearched,
  trackCoachPlayerSelected,
} from "@/analytics/analytics";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import Sidebar from "@/components/Sidebar";
import { PAGE_SUBTITLE_CLASS, PAGE_TITLE_CLASS } from "@/constants/design";

const SESSION_KEY = "recruit_ai_coach_active_profile";

interface LinkedProfile {
  jgsProfileId: string;
  studentId?: string;
  displayName?: string;
  jgsData?: {
    player_name?: string;
    state?: string;
    class_year?: string | number;
    rank?: string | number;
    scoring_differential?: string | number;
    class_rank?: string | number;
  };
  ajgaData?: {
    ranking?: string | number;
    points?: string | number;
  };
}

/* ------------------------------------------------------------------ */
/*  Link Player Modal                                                  */
/* ------------------------------------------------------------------ */

interface LinkPlayerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked: () => Promise<void>;
}

function LinkPlayerModal({ open, onOpenChange, onLinked }: LinkPlayerModalProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedJgsProfile, setSelectedJgsProfile] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    setQuery("");
    setResults([]);
    setSearching(false);
    setHasSearched(false);
    setSelectedJgsProfile(null);
    setLinking(false);
  }, [open]);

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearching(true);
    setSelectedJgsProfile(null);
    try {
      const data = await endUserSearch(trimmed);
      trackCoachPlayerSearched({ query: trimmed, resultCount: data.length });
      setResults(data);
      setHasSearched(true);
    } catch (err: any) {
      toast.error(err?.message || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleLink = async () => {
    if (!selectedJgsProfile) return;
    setLinking(true);
    try {
      await coachLinkProfile(undefined, selectedJgsProfile);
      trackCoachPlayerLinked({
        playerName:
          results.find((r: any) => String(r.jgsProfile || "") === selectedJgsProfile)
            ?.player_name || "Unknown",
      });
      await onLinked();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Failed to link player");
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Player</DialogTitle>
          <DialogDescription>Search for a junior golfer to link to your account.</DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        <div className="flex gap-2">
          <Input
            placeholder="Search by player name..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button onClick={handleSearch} disabled={searching || !query.trim()}>
            {searching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-2">Search</span>
          </Button>
        </div>

        {/* Results */}
        {hasSearched &&
          (results.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No results found. Try a different name.
            </p>
          ) : (
            <div className="max-h-64 overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Player</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Rank</TableHead>
                    <TableHead className="text-right">Select</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((r: any, idx: number) => {
                    const id = String(r.jgsProfile || `${r.player_name}-${idx}`);
                    const isSelected = selectedJgsProfile === id;
                    return (
                      <TableRow key={id}>
                        <TableCell className="font-medium">{r.player_name || "Unknown"}</TableCell>
                        <TableCell>{r.state || "—"}</TableCell>
                        <TableCell>{r.class_year || "—"}</TableCell>
                        <TableCell>
                          {typeof r.rank === "number" ? `#${r.rank.toLocaleString()}` : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant={isSelected ? "default" : "outline"}
                            onClick={() => setSelectedJgsProfile(isSelected ? null : id)}
                          >
                            {isSelected ? "Selected" : "Select"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ))}

        {/* Link button */}
        <div className="flex justify-end pt-2">
          <Button onClick={handleLink} disabled={!selectedJgsProfile || linking}>
            {linking && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Link Player
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Coach Corner Page                                                  */
/* ------------------------------------------------------------------ */

const CoachCorner = () => {
  const { updatePlayerSummary, isSidebarOpen, closeSidebar, chatHistory } = useEndUser();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<LinkedProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<LinkedProfile | null>(null);

  const getDisplayName = (p: LinkedProfile) =>
    p.displayName || p.jgsData?.player_name || "Unknown Player";

  const activateProfile = useCallback(
    (profile: LinkedProfile) => {
      setActiveProfileId(profile.jgsProfileId);
      sessionStorage.setItem(SESSION_KEY, profile.jgsProfileId);
      trackCoachPlayerSelected({
        playerName: getDisplayName(profile),
        jgsProfileId: profile.jgsProfileId,
      });

      const jgs = profile.jgsData;
      updatePlayerSummary({
        playerName: getDisplayName(profile),
        jgsRank: jgs?.rank != null ? String(jgs.rank) : "N/A",
        classRank: jgs?.class_rank != null ? String(jgs.class_rank) : "N/A",
        classYear: jgs?.class_year != null ? String(jgs.class_year) : "N/A",
        scoringDifferential:
          jgs?.scoring_differential != null ? String(jgs.scoring_differential) : "N/A",
        isLinked: true,
      });

      toast.success(`Now chatting as ${getDisplayName(profile)}`);
    },
    [updatePlayerSummary]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await getCoachLinkedProfiles();
        if (cancelled) return;
        const list: LinkedProfile[] = data?.linkedProfiles ?? [];
        setProfiles(list);

        // Restore or auto-select active profile
        const saved = sessionStorage.getItem(SESSION_KEY);
        const savedProfile = saved ? list.find(p => p.jgsProfileId === saved) : null;

        if (savedProfile) {
          setActiveProfileId(savedProfile.jgsProfileId);
        } else if (list.length > 0) {
          activateProfile(list[0]);
        }
      } catch (err: any) {
        if (!cancelled) {
          toast.error(err?.message || "Failed to load linked profiles");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [activateProfile]);

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await coachUnlinkProfile(removeTarget.jgsProfileId);
      trackCoachPlayerUnlinked({ playerName: getDisplayName(removeTarget) });
      const updated = profiles.filter(p => p.jgsProfileId !== removeTarget.jgsProfileId);
      setProfiles(updated);

      if (activeProfileId === removeTarget.jgsProfileId) {
        if (updated.length > 0) {
          activateProfile(updated[0]);
        } else {
          setActiveProfileId(null);
          sessionStorage.removeItem(SESSION_KEY);
          updatePlayerSummary({
            playerName: "No Player Selected",
            jgsRank: "N/A",
            classRank: "N/A",
            classYear: "N/A",
            scoringDifferential: "N/A",
            isLinked: false,
          });
        }
      }

      toast.success(`${getDisplayName(removeTarget)} removed`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to remove player");
    } finally {
      setRemoveTarget(null);
    }
  };

  const handleLinkSuccess = useCallback(async () => {
    try {
      const data = await getCoachLinkedProfiles();
      const list: LinkedProfile[] = data?.linkedProfiles ?? [];
      setProfiles(list);

      // If no active profile, auto-activate the first one
      if (!activeProfileId && list.length > 0) {
        activateProfile(list[0]);
      }

      toast.success("Player linked successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to refresh profiles");
    }
  }, [activeProfileId, activateProfile]);

  // ---------- Render ----------

  const sidebarEl = (
    <Sidebar
      chatHistory={chatHistory}
      onNewChat={() => navigate("/chat")}
      onClose={closeSidebar}
      onSelectChat={id => navigate(`/chat?thread=${id}`)}
    />
  );

  if (loading) {
    return (
      <TwoPaneLayout
        isSidebarOpen={isSidebarOpen}
        onCloseSidebar={closeSidebar}
        sidebar={sidebarEl}
        main={
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        }
      />
    );
  }

  return (
    <TwoPaneLayout
      isSidebarOpen={isSidebarOpen}
      onCloseSidebar={closeSidebar}
      sidebar={sidebarEl}
      main={
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className={PAGE_TITLE_CLASS}>Coach's Corner</h1>
              <p className={PAGE_SUBTITLE_CLASS}>Manage your players</p>
            </div>
            <Button onClick={() => setShowLinkModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add player
            </Button>
          </div>

          {/* Empty state */}
          {profiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Users className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-1">No players linked yet</h2>
              <p className="text-muted-foreground mb-6">Add your first player to get started.</p>
              <Button onClick={() => setShowLinkModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add player
              </Button>
            </div>
          ) : (
            /* Player card grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map(profile => {
                const isActive = activeProfileId === profile.jgsProfileId;
                const name = getDisplayName(profile);
                const jgs = profile.jgsData;
                const ajga = profile.ajgaData;

                return (
                  <Card
                    key={profile.jgsProfileId}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      isActive ? "ring-2 ring-primary border-primary" : ""
                    }`}
                    onClick={() => activateProfile(profile)}
                  >
                    <CardContent className="p-4">
                      {/* Name row */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-lg font-bold truncate">{name}</span>
                          {isActive && (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 shrink-0">
                              Active
                            </Badge>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={e => e.stopPropagation()}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={e => {
                                e.stopPropagation();
                                setRemoveTarget(profile);
                              }}
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Stats grid */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span>{jgs?.state || "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <GraduationCap className="h-3.5 w-3.5 shrink-0" />
                          <span>{jgs?.class_year ?? "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Trophy className="h-3.5 w-3.5 shrink-0" />
                          <span>Rank {jgs?.rank ?? "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <TrendingUp className="h-3.5 w-3.5 shrink-0" />
                          <span>{jgs?.scoring_differential ?? "N/A"}</span>
                        </div>
                      </div>

                      {/* AJGA section */}
                      {ajga && (ajga.ranking != null || ajga.points != null) && (
                        <div className="border-t mt-3 pt-3 flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Award className="h-3.5 w-3.5 shrink-0" />
                          <span>
                            AJGA {ajga.ranking != null ? `Rank ${ajga.ranking}` : ""}
                            {ajga.ranking != null && ajga.points != null ? " | " : ""}
                            {ajga.points != null ? `${ajga.points} pts` : ""}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Remove confirmation dialog */}
          <AlertDialog
            open={!!removeTarget}
            onOpenChange={open => {
              if (!open) setRemoveTarget(null);
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Remove {removeTarget ? getDisplayName(removeTarget) : ""}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This will unlink them from your account. Chat history will be preserved.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleRemove}>Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Link player modal */}
          <LinkPlayerModal
            open={showLinkModal}
            onOpenChange={setShowLinkModal}
            onLinked={handleLinkSuccess}
          />
        </div>
      }
    />
  );
};

export default CoachCorner;
